import React, { FC, ReactNode } from "react";
import {
  FeedRowAmount,
  FeedRowButton,
  FeedRowGroup,
  FeedRowIconTile,
  FeedRowOverline,
  FeedRowSkeletonLine,
  FeedRowSkeletonTile,
  FeedRowSubtitle,
  FeedRowSurface,
  FeedRowText,
  FeedRowTitle,
  V3FeedRowAmountTone,
  V3FeedRowIconTone,
} from "./FeedRow.styled";

export type { V3FeedRowAmountTone, V3FeedRowIconTone };

export interface V3FeedRowProps {
  icon: ReactNode;
  iconTone: V3FeedRowIconTone;
  title: string;
  subtitle?: string;
  amount?: string;
  amountTone?: V3FeedRowAmountTone;
  overline?: string;
  onClick?: () => void;
  /** Renders a non-interactive loading shell in place of the row content. */
  placeholder?: boolean;
  testID?: string;
}

export const V3FeedRow: FC<V3FeedRowProps> = ({
  icon,
  iconTone,
  title,
  subtitle,
  amount,
  amountTone = "neutral",
  overline,
  onClick,
  placeholder,
  testID = "v3-feed-row",
}) => {
  if (placeholder) {
    return (
      <FeedRowGroup>
        {overline && <FeedRowOverline>{overline}</FeedRowOverline>}
        <FeedRowSurface aria-hidden={true} data-testid={`${testID}-placeholder`}>
          <FeedRowSkeletonTile />
          <FeedRowText>
            <FeedRowSkeletonLine $width={60} />
            <FeedRowSkeletonLine $width={40} />
          </FeedRowText>
        </FeedRowSurface>
      </FeedRowGroup>
    );
  }

  const content = (
    <>
      <FeedRowIconTile $tone={iconTone} aria-hidden={true}>
        {icon}
      </FeedRowIconTile>
      <FeedRowText>
        <FeedRowTitle>{title}</FeedRowTitle>
        {subtitle && <FeedRowSubtitle>{subtitle}</FeedRowSubtitle>}
      </FeedRowText>
      {amount && <FeedRowAmount $tone={amountTone}>{amount}</FeedRowAmount>}
    </>
  );

  return (
    <FeedRowGroup>
      {overline && <FeedRowOverline>{overline}</FeedRowOverline>}
      {onClick ? (
        <FeedRowButton type="button" onClick={onClick} data-testid={testID}>
          {content}
        </FeedRowButton>
      ) : (
        <FeedRowSurface data-testid={testID}>{content}</FeedRowSurface>
      )}
    </FeedRowGroup>
  );
};
